import React, { useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SkeletonLines from '~/components/core/Skeletons/SkeletonLines';
import type {
  QueueVoicemailConfigurationInfo,
  QueueVoicemailConfigurationParams,
} from '~/server_shared/openapi/data-contracts';
import { isHiddenRole } from '~/Utils';

import CardDialog from '../../../../CardDialog';
// Both modules are .jsx without type declarations.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import CheckboxFormikUntyped from '../../../../CheckboxFormik';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { TextFieldFormik as TextFieldFormikUntyped } from '../../../../TextFieldFormik';
import { useSysconfig } from '../../../SystemConfigurationScreen';
import VoiceMessageConfigurationDialog from '../../PhoneConfigurationTab/VoiceMessageConfigurationDialog/VoiceMessageConfigurationDialog';

import { useStyles } from '../../../../../assets/styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CheckboxFormik = CheckboxFormikUntyped as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TextFieldFormik = TextFieldFormikUntyped as any;

const ALLOWED_TOKENS = { queue_name: { desc: 'Queue Name' } };

const FORM_IDS = {
  HAS_GREETING_OVERRIDE: '_has_greeting_override',
  HAS_USER_OVERRIDE: '_has_user_override',
  GREETING_TYPE: 'voicemail_message_configuration.configuration_type',
  GREETING_TEXT: 'voicemail_message_configuration.message_text',
  VOICE_FILE_OBJECT: 'voicemail_message_configuration.file',
  STORED_FILE_ID: 'voicemail_message_configuration.stored_file_id',
  NOTIFICATION_USER: 'notification_user_id',
};

interface UserOption {
  id: string;
  username: string;
  role: { role_type: string };
}

interface QueueVoicemailConfigDialogProps {
  queueName: string;
  existing?: QueueVoicemailConfigurationInfo | null;
  loading?: boolean;
  onSubmit: (values: QueueVoicemailConfigurationParams) => Promise<void>;
  onClearAll: () => Promise<void>;
  onCancel: () => void;
}

const QueueVoicemailConfigDialog: React.FC<QueueVoicemailConfigDialogProps> = ({
  queueName,
  existing,
  loading = false,
  onSubmit,
  onClearAll,
  onCancel,
}) => {
  const { classes } = useStyles();
  const { users } = useSysconfig() as { users: UserOption[] };
  const [allowSavingWithoutNewFile, setAllowSavingWithoutNewFile] = useState<boolean>(true);

  const existingGreeting = existing?.voicemail_message_configuration ?? null;
  const existingUser = existing?.notification_user_id ?? null;

  if (loading) {
    return (
      <CardDialog title={`Voicemail Configuration — ${queueName}`} isDialog maxWidth="sm" fullWidth onClose={onCancel}>
        <SkeletonLines count={3} widths={['75%', '66%', '66%']} className="py-8" />
      </CardDialog>
    );
  }

  return (
    <Formik
      initialValues={{
        [FORM_IDS.HAS_GREETING_OVERRIDE]: !!existingGreeting,
        [FORM_IDS.HAS_USER_OVERRIDE]: !!existingUser,
        voicemail_message_configuration: {
          configuration_type: existingGreeting?.configuration_type || 'text_message',
          message_text: existingGreeting?.message_text || '',
          stored_file_id: existingGreeting?.stored_file_id || null,
          file: null,
        },
        notification_user_id: existingUser || '',
      }}
      validationSchema={Yup.object().shape({
        voicemail_message_configuration: Yup.object().when(FORM_IDS.HAS_GREETING_OVERRIDE, {
          is: true,
          then: () =>
            Yup.object().shape({
              configuration_type: Yup.string().oneOf(['text_message', 'uploaded_text_message']).required(),
              message_text: Yup.string().when('configuration_type', {
                is: 'text_message',
                then: (schema) => schema.required('Greeting text is required'),
              }),
              stored_file_id: Yup.mixed().when('configuration_type', {
                is: 'uploaded_text_message',
                then: (schema) =>
                  allowSavingWithoutNewFile ? schema.nullable() : schema.required('Voice file is required'),
              }),
            }),
        }),
        notification_user_id: Yup.string().when(FORM_IDS.HAS_USER_OVERRIDE, {
          is: true,
          then: (schema) => schema.required('Notification user is required when override is enabled'),
        }),
      })}
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit({
            voicemail_message_configuration: values[FORM_IDS.HAS_GREETING_OVERRIDE]
              ? {
                  configuration_type: values.voicemail_message_configuration.configuration_type,
                  message_text:
                    values.voicemail_message_configuration.configuration_type === 'text_message'
                      ? values.voicemail_message_configuration.message_text || null
                      : null,
                  stored_file_id:
                    values.voicemail_message_configuration.configuration_type === 'uploaded_text_message'
                      ? values.voicemail_message_configuration.stored_file_id || null
                      : null,
                }
              : null,
            notification_user_id: values[FORM_IDS.HAS_USER_OVERRIDE] ? values.notification_user_id || null : null,
          });
          onCancel();
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit: formikHandleSubmit }) => {
        return (
          <CardDialog
            title={`Voicemail Configuration — ${queueName}`}
            isDialog
            maxWidth="sm"
            fullWidth
            onClose={onCancel}
            preventClose={isSubmitting}
          >
            <Typography variant="body2" className="mb-8">
              Unchecked sections inherit the org-level default at runtime.
            </Typography>

            <div className="mb-4">
              <CheckboxFormik id={FORM_IDS.HAS_USER_OVERRIDE} label="Override notification user for this queue" />
            </div>
            <ConditionalField fieldId={FORM_IDS.HAS_USER_OVERRIDE}>
              <TextFieldFormik id={FORM_IDS.NOTIFICATION_USER} select label="Notification user" fullWidth>
                {users
                  .filter((u) => !isHiddenRole(u.role.role_type))
                  .map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.username}
                    </MenuItem>
                  ))}
              </TextFieldFormik>
            </ConditionalField>

            <div className="mb-4 mt-8">
              <CheckboxFormik id={FORM_IDS.HAS_GREETING_OVERRIDE} label="Override greeting for this queue" />
            </div>
            <ConditionalField fieldId={FORM_IDS.HAS_GREETING_OVERRIDE}>
              <VoiceMessageConfigurationDialog
                voiceMessageConfiguration={
                  existingGreeting
                    ? {
                        configuration_type: existingGreeting.configuration_type || 'text_message',
                        message_text: existingGreeting.message_text || '',
                        stored_file_id: existingGreeting.stored_file_id || null,
                        permanent_audio_file_url: existingGreeting.permanent_audio_file_url || null,
                      }
                    : null
                }
                textAreaLabel="Voicemail greeting"
                textAreaId={FORM_IDS.GREETING_TEXT}
                voiceFileInputId={FORM_IDS.VOICE_FILE_OBJECT}
                storedFileIdPath={FORM_IDS.STORED_FILE_ID}
                radioButtonGroupId={FORM_IDS.GREETING_TYPE}
                allowedTokens={ALLOWED_TOKENS}
                onReplaceExistingRecording={() => {
                  setAllowSavingWithoutNewFile(false);
                }}
              />
            </ConditionalField>

            <div className={classes.buttonsContainer}>
              <Button
                color="secondary"
                disabled={isSubmitting || (!existingGreeting && !existingUser)}
                onClick={async () => {
                  await onClearAll();
                  onCancel();
                }}
              >
                Clear All Overrides
              </Button>
              <CancelButton disabled={isSubmitting} onClick={onCancel} />
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={(e) => {
                  e.preventDefault();
                  formikHandleSubmit();
                }}
              >
                Save
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

interface ConditionalFieldProps {
  fieldId: string;
  children: React.ReactNode;
}

const ConditionalField: React.FC<ConditionalFieldProps> = ({ fieldId, children }) => {
  const { values } = useFormikContext<Record<string, unknown>>();
  if (!values[fieldId]) {
    return null;
  }
  return <div className="mb-4 ml-8">{children}</div>;
};

export default QueueVoicemailConfigDialog;
